import { memo, useEffect } from 'react';
import { StyleSheet } from 'react-native';
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';

import { useNavigation } from '@react-navigation/native';

import { Button } from '@/components/ButtonComponent';
import { Text } from '@/components/TextComponent';
import { Box, Image, Row, TouchableBox } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { common } from '@/styles/styles';

import { useWhatsNewStore } from '../hooks/use-whats-new-store';
import { useWhatsNewVisibility } from '../hooks/use-whats-new-visibility';

const WHATS_NEW_LOBBY_CARD_HEIGHT = 72;
const WHATS_NEW_LOBBY_CARD_TOP_SPACING = 14;
const WHATS_NEW_LOBBY_CARD_BOTTOM_SPACING = 6;
export const WHATS_NEW_LOBBY_CARD_TOTAL_HEIGHT =
    WHATS_NEW_LOBBY_CARD_HEIGHT + WHATS_NEW_LOBBY_CARD_TOP_SPACING + WHATS_NEW_LOBBY_CARD_BOTTOM_SPACING;
const ANIMATION_DURATION = 350;

export const WhatsNewLobbyCard = memo(() => {
    const navigation = useNavigation();
    const { hasUnseen, lobbyCardData } = useWhatsNewVisibility();
    const isLobbyReady = useWhatsNewStore(state => state.isLobbyReady);

    const height = useSharedValue(0);
    const opacity = useSharedValue(0);

    useEffect(() => {
        if (!isLobbyReady) {
            //Skip animating in while lobby not ready, so we avoid slughish animation while
            //heavy lobby components might be rendering
            return;
        }
        const duration = hasUnseen && navigation.isFocused() ? ANIMATION_DURATION : 0;
        height.value = withTiming(
            hasUnseen
                ? WHATS_NEW_LOBBY_CARD_HEIGHT + WHATS_NEW_LOBBY_CARD_TOP_SPACING + WHATS_NEW_LOBBY_CARD_BOTTOM_SPACING
                : 0,
            {
                duration,
            }
        );
        opacity.value = withTiming(hasUnseen ? 1 : 0, { duration });
    }, [hasUnseen, isLobbyReady, height, opacity, navigation]);

    const containerStyle = useAnimatedStyle(() => ({
        height: height.value,
        opacity: opacity.value,
    }));

    return (
        <Animated.View style={[containerStyle, common.noOverflow]}>
            <Box style={styles.containerSpacing}>
                <MaxWidthWrapper maxWidth="100%">
                    <TouchableBox
                        onPress={() => navigation.navigate('WhatsNewScreen')}
                        marginHorizontal={'s16'}
                        style={styles.container}
                        height={WHATS_NEW_LOBBY_CARD_HEIGHT}
                    >
                        <Row
                            backgroundColor={ds.colorToken.background.neutral.subtler}
                            flex={1}
                            gap="s16"
                            padding="s16"
                            alignItems={'center'}
                        >
                            {lobbyCardData.image ? (
                                <Image source={{ uri: lobbyCardData.image }} style={styles.image} />
                            ) : null}
                            <Box flex={1}>
                                <Text
                                    color={ds.colorToken.text.primary}
                                    variant={'buttonLabel'}
                                    lineHeight={20}
                                    letterSpacing={-0.23}
                                >
                                    {lobbyCardData.title}
                                </Text>
                                <Text
                                    color={ds.colorToken.text.secondary}
                                    variant={'bodyMedium'}
                                    lineHeight={20}
                                    letterSpacing={-0.23}
                                >
                                    {lobbyCardData.description}
                                </Text>
                            </Box>
                            <Box pointerEvents="none">
                                <Button
                                    label={lobbyCardData.buttonLabel ?? 'View'}
                                    shape="rect"
                                    size="s"
                                    hierarchy="secondary"
                                    style={styles.buttonContainer}
                                    textStyle={styles.buttonLabel}
                                />
                            </Box>
                        </Row>
                        <Box position={'absolute'} style={[StyleSheet.absoluteFill, styles.border]} />
                    </TouchableBox>
                </MaxWidthWrapper>
            </Box>
        </Animated.View>
    );
});

WhatsNewLobbyCard.displayName = 'WhatsNewLobbyCard';

const styles = StyleSheet.create({
    image: {
        width: 28,
        height: 28,
    },
    container: {
        borderRadius: 19,
        overflow: 'hidden',
    },
    border: {
        position: 'absolute',
        zIndex: 1,
        width: '100%',
        height: '100%',
        borderRadius: 19,
        borderWidth: 1,
        borderColor: ds.colorValue.border.subtle,
        pointerEvents: 'none',
    },
    buttonContainer: {
        borderRadius: 11,
        paddingHorizontal: 14,
        paddingVertical: 9,
    },
    buttonLabel: {
        lineHeight: 18,
        letterSpacing: -0.08,
    },
    containerSpacing: {
        paddingTop: WHATS_NEW_LOBBY_CARD_TOP_SPACING,
        paddingBottom: WHATS_NEW_LOBBY_CARD_BOTTOM_SPACING,
    },
});
